<template>
  <div class="slot-days" v-if="availableSlotsByVisitType && availableSlotsByVisitType.length > 0">
    <div class="header">
      
      <div v-for="(daySlots, n) in availableDays" :key="`day-${n}`">
        
        <template v-if="daySlots?.length > 0">
          <span>{{ formatWeekday(daySlots[0].scheduleDate) }}</span>
          <span>{{ formatMonthDay(daySlots[0].scheduleDate) }}</span>
        </template>
        <template v-else>
          <span>Nothing<br />available</span><!-- fallback -->
        </template>
      </div>
    </div>
    
    <div class="body">
      
      <div v-for="(item, n) in resCurrentDays" :key="`res-${n}`">
        <ul>
          <li v-for="(slot, m) in item" :key="slot.reservationId > 0 ? slot.reservationId : (m * -1)">
            <div v-if="slot.availableSlotCount > 0">
              <button type="button" :aria-labelledby="`slot${slot.reservationId}`"
                      class="app-btn-selectable slot-option" :class="{ '__active': selectedSlot === slot.reservationId }"
                      :ref="el => { if (el) slotRefs[slot.reservationId] = el }"
                      @click="selectSlot(slot)">
                <span aria-hidden="true" :ref="el => { if (el) slotTimeRefs[slot.reservationId] = el }">{{ formatTime(slot.startTime) }}</span>
                <span aria-hidden="true" :ref="el => { if (el) slotLocationRefs[slot.reservationId] = el }">{{ slot.location }}</span>
                <template v-if="slot && slot.scheduleDate">
                  <p class="sr-only" :id="`slot${slot.reservationId}`">
                    {{ formatWeekday(slot.scheduleDate) }} {{ formatMonthDay(slot.scheduleDate) }},
                    {{ formatTime(slot.startTime) }} at {{ slot.location }}
                  </p>
                  <div hidden>
                    <span :ref="el => { if (el) slotDateRefs[slot.reservationId] = el }">{{ slot.scheduleDate }}</span>
                  </div>
                </template>
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, computed } from 'vue';
import { useStore } from 'vuex';
import { SLOTS_FILTER, SLOTS_GET_ALL } from '@/store/_commons';

import {formatWeekday, formatMonthDay, formatTime} from '../../../utils/globalFilters'


const store = useStore();

const availableSlotsByVisitType = computed(() => store.state.user.availableSlotsByVisitType || []);
const resAvailableSlots = computed(() => store.state.user.resAvailableSlots || []);
const resSlot = computed(() => store.state.user.resSlot || []);
const resCurrentDays = computed(() => store.state.user.resCurrentDays || []);

const mySlot = ref(-1);
const resCurrentDaysHeader = ref([]);
const selectedSlotDatetime = ref({});
const selectedSlot = ref(null);

const slotRefs = ref({});
const slotTimeRefs = ref({});
const slotLocationRefs = ref({});
const slotDateRefs = ref({});

const emit = defineEmits(['doNext']);

// Computed property 
const availableDays = computed(() => {
  return resCurrentDaysHeader.value.filter(daySlots => daySlots && daySlots.length > 0);
});

const setStoreProperty = (payload) => store.dispatch('user/setStoreProperty', payload);

const setReservationDetails = (payload) => store.dispatch('user/setReservationDetails', payload);

// Watchers
watch(mySlot, (newVal, oldVal) => {
  if (oldVal > 0) toggleActiveClass(oldVal, false);
  if (newVal > 0) toggleActiveClass(newVal, true);

  setStoreProperty({ property: 'resSlot', value: newVal });

  const data = [];
  if (newVal > 0 && slotLocationRefs.value[newVal] && slotDateRefs.value[newVal] && slotTimeRefs.value[newVal]) {
    data.push({ property: 'serviceLocation', value: { location: slotLocationRefs.value[newVal].innerText } });
    data.push({ property: 'serviceDate', value: slotDateRefs.value[newVal].innerText });
    data.push({ property: 'serviceTime', value: slotTimeRefs.value[newVal].innerText });
    data.push({ property: 'internalServiceTime', value: selectedSlotDatetime.value.startTime });
  }
  setReservationDetails(data);

  emit('doNext', newVal > 0);
});

watch(resAvailableSlots, () => {
  store.dispatch(SLOTS_FILTER);
});

watch(resCurrentDays, (newVal) => {
  resCurrentDaysHeader.value = [];
  if (newVal && newVal.length > 0) {
    nextTick(() => {
      resCurrentDaysHeader.value = newVal;
    });
  }
}, { immediate: true });

const selectSlot = (slot) => {
  mySlot.value = slot.reservationId;
  selectedSlot.value = slot.reservationId;
  selectedSlotDatetime.value = {
    scheduleDate: slot.scheduleDate,
    startTime: slot.startTime,
  };
};

const toggleActiveClass = (id, active) => {
  const elem = slotRefs.value[id];
  if (elem) {
    if (active) {
      elem.classList.add('__active');
    } else {
      elem.classList.remove('__active');
    }
  }
};

onMounted(() => {
  store.dispatch(SLOTS_GET_ALL);
});

watch(resSlot, (newVal) => {
  if (newVal > 0) {
    selectedSlot.value = newVal;
    mySlot.value = newVal;
  }
}, { immediate: true });
</script>


<style scoped lang="scss">
.slot-days {
  --col-border: 1px solid var(--ri-gray);
  --col-days: 6;
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  justify-content: center;
  margin: 1em 0 2em;
  .header, .body {
    display: grid;
    text-align: center;
    position: relative;
    // see store's getNumOfColumns()
    @media only screen and (max-width: 740px) {
      --col-days: 3;
    }
    // place AFTER the --col-days media query (above)
    grid-template-columns: repeat(var(--col-days), 1fr);
  }
  .header {
    background-color: var(--ri-light-gray);
    box-shadow: 0 3px 4px var(--ri-gray);
    @extend %font-sans-bold;
    padding: 1em 14px 1em 0;
    z-index: z(slot-header);
    div {
      span:first-child {
        @extend %font-sans-regular;
        display: block;
      }
    }
  }
  .body {
    background-color: white;
    border-right: var(--col-border);
    border-bottom: var(--col-border);
    height: 355px;
    overflow-y: auto;
    z-index: z(slot-body);
    ul {
      border-left: var(--col-border);
      height: 100%;
      li:first-child {
        padding-top: 0.75em;
      }
    }
    .slot-option {
      width: 120px;
    }
    @media only screen and (max-width: 880px) {
      .slot-option {
        width: 104px;
      }
    }
  }
  @media only screen and (max-width: 425px) {
    // one-off whitespace here and there in design...
    margin-left: calc(var(--padding-horizontal) * -1);
    margin-right: calc(var(--padding-horizontal) * -1);
  }
}
</style>
